import {makeAutoObservable, observable} from 'mobx'
import {logger, SanitizedHtml} from 'yougile-util'
import {Hub, TaskHubData} from 'yougile-model'
import {Backend} from '@common/backend'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {wrapLinks} from '@components/right-panel/task-description/wrap-links'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'

export interface State {
  edit: boolean
  content: string // orig task desc, need for cancel
  contentChanged: string // desc that changed by editor
}

const initState: State = {
  edit: false,
  content: '',
  contentChanged: ''
}

export class TaskDescriptionStore {
  state: State = initState
  taskHub: Hub

  constructor(taskHub: Hub) {
    this.taskHub = taskHub

    makeAutoObservable(this, {state: observable.deep}, {autoBind: true})
  }

  setEdit(edit: boolean): void {
    if (edit === this.state.edit) {
      return
    }

    if (edit) {
      void Tracking.send(TrackingEvent.taskDescriptionEdit)
    }
    this.state.edit = edit
  }

  save(forceDescription?: string): void {
    this.setEdit(false)

    const desc = forceDescription ?? this.state.contentChanged ?? ''
    const preparedDesc = desc.replaceAll('&nbsp;', ' ')

    if (!this.taskHub?.id || this.state.content === preparedDesc) {
      return
    }

    const processedContent = wrapLinks(preparedDesc)

    if (this.state.content === processedContent) {
      this.state.content = processedContent
      this.state.contentChanged = processedContent
      return
    }

    try {
      const taskHubData = this.taskHub.data as TaskHubData
      const date = +new Date()
      taskHubData.descriptionModifiedAt = date

      this.taskHub.description = processedContent

      Task.commitWithCheckPhantom(this.taskHub)

      this.state.content = processedContent
      this.state.contentChanged = processedContent

      // send history
      void Backend.postJSON<{saveStatus: boolean}>('/data/description/save', {
        ...Backend.loadCred(),
        companyId: this.taskHub.companyId,
        userId: globals.currentUserId,
        taskId: this.taskHub.id,
        description: processedContent,
        date
      })
    } catch (e) {
      logger.error(e)
    }
  }

  saveContent(): void {
    if (!this.state.edit) {
      return
    }

    void Tracking.send(TrackingEvent.taskDescriptionSave)

    void this.save()
  }

  cancelEdit(): void {
    this.setEdit(false)
    this.state.contentChanged = this.state.content
  }

  onChange(data: string): void {
    if (this.state.edit) {
      this.state.contentChanged = data
    }
  }

  updateContent(html: SanitizedHtml): void {
    this.state.content = html
    this.state.contentChanged = html
  }

  onEscape(): boolean {
    this.cancelEdit()
    return true
  }
}

const [TaskDescriptionContext, useTaskDescription] =
  createUseStore(TaskDescriptionStore)
export {TaskDescriptionContext, useTaskDescription}
